import { useMemo, useState, type MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, CornerDownRight, Highlighter, Inbox, MessageSquare, PenLine, RefreshCw, type LucideIcon } from 'lucide-react';
import { alsTag, datumKurz, feedbackAlle, tagIso, wannText, type AutorArt, type FeedbackMitMail } from '../../lib/planung';
import { Avatar } from '../../components/Planung/Avatar';

type Filter = 'offen' | 'neu' | 'alle';

const AUTOR: Record<AutorArt, string> = { extern: 'per Link', team: 'Redaktion', intern: 'im Mailtool' };

/** Offen = Kommentar oder Vorschlag, noch nicht erledigt, keine Antwort im Faden. */
/** Alte Einträge können unvollständig sein — nur echte Änderungen zählen. */
const aenderungenVon = (e: FeedbackMitMail) => (Array.isArray(e.aenderungen) ? e.aenderungen.filter((a) => !!a && typeof a === 'object') : []);

const istOffen = (e: FeedbackMitMail) => e.ergebnis === null && (e.art === 'kommentar' || e.art === 'vorschlag') && !e.antwort_auf;

function artInfo(e: FeedbackMitMail): { label: string; badge: string; Icon: LucideIcon } {
  if (e.art === 'passt') return { label: 'Passt so', badge: 'badge-success', Icon: CheckCircle2 };
  if (e.antwort_auf) return { label: 'Antwort', badge: 'badge-neutral', Icon: CornerDownRight };
  if (e.art === 'vorschlag') {
    const n = aenderungenVon(e).length + (e.betreff_vorschlag ? 1 : 0);
    return { label: `Änderungsvorschlag (${n} ${n === 1 ? 'Stelle' : 'Stellen'})`, badge: 'badge-info', Icon: PenLine };
  }
  return { label: 'Kommentar', badge: 'badge-neutral', Icon: MessageSquare };
}

function ErgebnisBadge({ e }: { e: FeedbackMitMail }) {
  const wer = e.erledigt_von_name ? ` von ${e.erledigt_von_name}` : '';
  if (e.ergebnis === 'erledigt') return <span className="badge badge-success" title={`Erledigt${wer}`}>Erledigt</span>;
  if (e.ergebnis === 'uebernommen') return <span className="badge badge-success" title={`Übernommen${wer}`}>Übernommen</span>;
  if (e.ergebnis === 'abgelehnt') return <span className="badge badge-muted" title={`Abgelehnt${wer}`}>Abgelehnt</span>;
  if (istOffen(e)) return <span className="badge badge-warning">Offen</span>;
  return null;
}

const kuerzen = (t: string | null | undefined, n: number) => {
  const s = typeof t === 'string' ? t : '';
  return s.length > n ? `${s.slice(0, n - 1).trimEnd()} …` : s;
};

function tagTitel(iso: string, heute: Date): string {
  const d = alsTag(iso)!;
  const tage = Math.round((alsTag(heute)!.getTime() - d.getTime()) / 86_400_000);
  if (tage === 0) return 'Heute';
  if (tage === 1) return 'Gestern';
  return d.getFullYear() === heute.getFullYear() ? datumKurz(d) : `${datumKurz(d)}${d.getFullYear()}`;
}

function Eintrag({ e, onOeffnen }: { e: FeedbackMitMail; onOeffnen: (e: FeedbackMitMail) => void }) {
  const art = artInfo(e);
  const neu = !e.gelesen && e.autor_art !== 'team';
  const betreff = e.mail_betreff?.trim() || e.mail_name || 'Ohne Betreff';
  const zeit = new Date(e.created_at).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  const alleAenderungen = aenderungenVon(e);
  const aenderungen = alleAenderungen.filter((a) => a.entfernen || a.vorher_text !== a.nachher_text).slice(0, 2);
  const weitere = alleAenderungen.length - aenderungen.length;

  const klick = (ev: MouseEvent) => {
    if (window.getSelection()?.toString()) return;
    if ((ev.target as HTMLElement).closest('a, button')) return;
    onOeffnen(e);
  };

  return (
    <li className={`gm-rm${neu ? ' gm-rm--neu' : ''}`} onClick={klick}>
      <span className="gm-rm-avatar"><Avatar name={e.autor_name} groesse="md" /></span>
      <div className="gm-rm-inhalt">
        <div className="gm-rm-kopf">
          <strong className="gm-rm-name">{e.autor_name}</strong>
          <span className="gm-rm-wer">{AUTOR[e.autor_art]}</span>
          <span className={`badge ${art.badge}`}><art.Icon size={12} aria-hidden="true" />{art.label}</span>
          <ErgebnisBadge e={e} />
          {neu && <span className="gm-rm-neu">neu</span>}
          <time className="gm-rm-zeit" dateTime={e.created_at} title={wannText(e.created_at)}>{zeit}</time>
        </div>

        <button type="button" className="gm-rm-mail" onClick={() => onOeffnen(e)}>
          zu „{betreff}“
        </button>

        {e.zitat && <blockquote className="gm-rm-zitat">{kuerzen(e.zitat, 220)}</blockquote>}
        {e.text && <p className="gm-rm-text">{kuerzen(e.text, 600)}</p>}

        {e.betreff_vorschlag && (
          <p className="gm-rm-vorschlag"><span className="gm-rm-vorschlag-label">Neuer Betreff:</span> <ins>{kuerzen(e.betreff_vorschlag, 160)}</ins></p>
        )}
        {aenderungen.map((a, i) => (
          <p key={i} className="gm-rm-vorschlag">
            <del>{kuerzen(a.vorher_text, 140) || 'Bild'}</del>
            <span className="gm-rm-pfeil" aria-hidden="true">→</span>
            {a.entfernen ? (
              <>
                <span className="sr-only"> wird entfernt: </span>
                <em>{a.entfernen.art} entfernen</em>
              </>
            ) : (
              <>
                <span className="sr-only"> wird zu </span>
                <ins>{kuerzen(a.nachher_text, 140)}</ins>
              </>
            )}
          </p>
        ))}
        {weitere > 0 && <p className="gm-rm-weitere">… und {weitere} weitere {weitere === 1 ? 'Stelle' : 'Stellen'}</p>}

        {e.markierung && (
          <span className="pl-chip gm-rm-chip"><Highlighter size={13} aria-hidden="true" />mit Zeichenstift-Markierung</span>
        )}
      </div>
    </li>
  );
}

/** Reiter „Rückmeldungen": alle Kommentare und Vorschläge über alle Mails, neueste zuerst. */
export function RueckmeldungenReiter() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<Filter>('offen');
  const abfrage = useQuery({ queryKey: ['feedback-alle'], queryFn: () => feedbackAlle(), refetchInterval: 60_000 });

  const alle = useMemo(() => abfrage.data ?? [], [abfrage.data]);
  const zahl: Record<Filter, number> = {
    offen: alle.filter(istOffen).length,
    neu: alle.filter((e) => !e.gelesen && e.autor_art !== 'team').length,
    alle: alle.length,
  };

  const tage = useMemo(() => {
    const liste = alle
      .filter((e) => (filter === 'offen' ? istOffen(e) : filter === 'neu' ? !e.gelesen && e.autor_art !== 'team' : true))
      .sort((a, b) => b.created_at.localeCompare(a.created_at));
    const gruppen: { iso: string; eintraege: FeedbackMitMail[] }[] = [];
    for (const e of liste) {
      const iso = tagIso(alsTag(e.created_at)!);
      const letzte = gruppen[gruppen.length - 1];
      if (letzte && letzte.iso === iso) letzte.eintraege.push(e);
      else gruppen.push({ iso, eintraege: [e] });
    }
    return gruppen;
  }, [alle, filter]);

  const oeffnen = (e: FeedbackMitMail) => navigate(`/geplante-mails/${e.campaign_id}`);
  const heute = new Date();

  return (
    <div className="gm-reiter-inhalt">
      <div className="gm-werkzeug">
        <div className="seg-control" role="group" aria-label="Rückmeldungen filtern">
          {([['offen', 'Offen'], ['neu', 'Neu'], ['alle', 'Alle']] as [Filter, string][]).map(([id, label]) => (
            <button key={id} type="button" className={`seg-btn${filter === id ? ' active' : ''}`} aria-pressed={filter === id} onClick={() => setFilter(id)}>
              {label} <span className="gm-zahl">{zahl[id]}</span>
            </button>
          ))}
        </div>
        <span className="gm-werkzeug-hinweis">Neue Rückmeldungen gelten als gelesen, sobald jemand aus dem Team die Mail öffnet.</span>
      </div>

      {abfrage.isLoading ? (
        <div className="gm-laden"><div className="loading-spinner" /></div>
      ) : abfrage.isError && !abfrage.data ? (
        <div className="alert alert-danger" role="alert">
          <span>Die Rückmeldungen ließen sich nicht laden: {(abfrage.error as Error).message}</span>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => void abfrage.refetch()}><RefreshCw size={14} aria-hidden="true" /> Nochmal</button>
        </div>
      ) : tage.length === 0 ? (
        <div className="gm-leer">
          <Inbox size={32} aria-hidden="true" />
          <p>
            {filter === 'offen' ? 'Nichts offen — alle Rückmeldungen sind eingearbeitet.'
              : filter === 'neu' ? 'Keine neuen Rückmeldungen.'
                : 'Noch keine Rückmeldungen. Sobald Ansprechpersonen kommentieren oder etwas vorschlagen, steht es hier.'}
          </p>
        </div>
      ) : (
        <div className="gm-rm-tage">
          {tage.map((t) => (
            <section key={t.iso} className="gm-rm-tag">
              <h2 className="gm-rm-tag-titel">{tagTitel(t.iso, heute)}<span className="gm-rm-tag-zahl">{t.eintraege.length}</span></h2>
              <ol className="gm-rm-liste">
                {t.eintraege.map((e) => <Eintrag key={e.id} e={e} onOeffnen={oeffnen} />)}
              </ol>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
